import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Handshake, FilePdf, ArrowLeft, Envelope, PencilSimple, TrashSimple, MagnifyingGlass, CheckCircle, XCircle, ClockClockwise,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/dateUtils";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

const STATUS_META = {
  acik: { label: "Açık", cls: "bg-emerald-100 text-emerald-800", icon: ClockClockwise },
  kapali: { label: "Kapalı", cls: "bg-slate-200 text-slate-700", icon: CheckCircle },
};

const RESPONSE_META = {
  verildi: { label: "Verildi", cls: "bg-emerald-100 text-emerald-700" },
  verilmedi: { label: "Verilmedi", cls: "bg-red-100 text-red-700" },
  beklemede: { label: "Beklemede", cls: "bg-amber-100 text-amber-800" },
};

export default function RFQ() {
  const nav = useNavigate();
  const askConfirm = useConfirm();
  const { sid } = useParams();
  const { sites } = useSites();
  const isMerkez = sid === "merkez";
  const site = (sites || []).find((s) => s.id === sid);
  const siteLabel = isMerkez ? "Merkez / Genel" : (site ? `${site.code} · ${site.name}` : "");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [year, setYear] = useState("all");

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (year !== "all") params.year = year;
    if (q) params.q = q;
    params.site_id = sid;
    const { data } = await axios.get(`${API}/rfqs`, { params });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, year, sid]);
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const years = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.request_date) s.add(r.request_date.slice(0, 4));
    return Array.from(s).sort().reverse();
  }, [rows]);

  const { sort, toggleSort, sortedRows } = useSortableRows(rows, {
    defaultKey: "request_date", defaultDir: "desc",
    getters: {
      code: (r) => r.code || "",
      request_date: (r) => r.request_date || "",
      items_count: (r) => (r.items || []).filter(it => it.material_name).length,
      recipients_count: (r) => (r.recipients || []).length,
      status: (r) => r.status || "",
    },
  });

  const openPdf = (r) => {
    window.open(`${API}/rfqs/${r.id}/pdf`, "_blank", "noopener,noreferrer");
  };

  const del = async (r) => {
    const ok = await askConfirm({
      title: "Talebi Sil",
      description: `${r.code} numaralı teklif talebini silmek istiyor musunuz? Bu işlem geri alınamaz.`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/rfqs/${r.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Shell
      title="Teklif Talepleri"
      subtitle={siteLabel ? `Satın Alma Yönetimi · ${siteLabel}` : "Satın Alma Yönetimi"}
      hideSiteSelector
      actions={
        <Button
          data-testid="add-rfq"
          onClick={() => nav(`/teklif-talepleri/yeni?site=${sid}`)}
          className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white"
        >
          <Plus size={16} className="mr-2" /> Yeni Teklif Talebi
        </Button>
      }
    >
      <Link to="/teklif-talepleri" data-testid="rfq-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1B4776] mb-3">
        <ArrowLeft size={14} /> Şantiyeler
      </Link>
      {/* Filtreler */}
      <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Kod, malzeme veya firma ara..."
              data-testid="rfq-search"
              className="pl-8 rounded-none sm:rounded-sm"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="rfq-status-filter">
              <SelectValue placeholder="Durum" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="acik">Açık</SelectItem>
              <SelectItem value="kapali">Kapalı</SelectItem>
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="rfq-year-filter">
              <SelectValue placeholder="Yıl" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Yıllar</SelectItem>
              {years.map((y) => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Liste */}
      {rows.length === 0 ? (
        <Card className="p-16 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white text-center" data-testid="rfq-empty">
          <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#1B4776] mb-6">
            <Handshake size={32} weight="duotone" className="text-[#95C11F]" />
          </div>
          <div className="text-lg font-bold text-slate-900 font-heading">Henüz teklif talebi yok</div>
          <div className="text-sm text-slate-600 mt-1">Sağ üstteki "Yeni Teklif Talebi" ile ilk kaydınızı oluşturun.</div>
        </Card>
      ) : (
        <Card className="rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                  <SortableTh label="Talep No" k="code" sort={sort} onSort={toggleSort} className="text-left py-3 px-4" />
                  <SortableTh label="Tarih" k="request_date" sort={sort} onSort={toggleSort} className="text-left py-3 px-4" />
                  <th className="text-left py-3 px-4 font-bold">Malzemeler</th>
                  <SortableTh label="Kalem" k="items_count" sort={sort} onSort={toggleSort} className="text-right py-3 px-4" align="right" />
                  <SortableTh label="Firma" k="recipients_count" sort={sort} onSort={toggleSort} className="text-right py-3 px-4" align="right" />
                  <SortableTh label="Durum" k="status" sort={sort} onSort={toggleSort} className="text-left py-3 px-4" />
                  <th className="text-right py-3 px-4 font-bold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, i) => {
                  const st = STATUS_META[r.status] || STATUS_META.acik;
                  const StIcon = st.icon;
                  const materialsPreview = (r.items || [])
                    .filter(it => it.material_name)
                    .slice(0, 3)
                    .map(it => it.material_name)
                    .join(", ");
                  const totalItems = (r.items || []).filter(it => it.material_name).length;
                  const totalRecipients = (r.recipients || []).length;
                  const verildi = (r.recipients || []).filter(x => x.response === "verildi").length;
                  return (
                    <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${i % 2 ? "bg-slate-50/30" : ""}`} data-testid={`rfq-row-${i}`}>
                      <td className="py-3 px-4 font-mono font-bold text-slate-900">{r.code}</td>
                      <td className="py-3 px-4 text-slate-600">{fmtDate(r.request_date)}</td>
                      <td className="py-3 px-4 text-slate-700 max-w-md truncate">
                        {materialsPreview || <span className="italic text-slate-400">Kalem yok</span>}
                        {totalItems > 3 && <span className="text-slate-400"> +{totalItems - 3}</span>}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-slate-700">{totalItems}</td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono text-slate-700">{verildi}</span>
                        <span className="text-slate-400 font-mono">/{totalRecipients}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${st.cls}`}>
                          <StIcon size={11} weight="fill" />
                          {st.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => nav(`/gelen-teklifler/${sid}?rfq=${r.id}&code=${encodeURIComponent(r.code || "")}`)}
                            title="Gelen Teklifler"
                            data-testid={`rfq-offers-${i}`}
                            className="h-8 px-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1B4776] hover:bg-[#1B4776]/10"
                          >
                            <Envelope size={14} /> Teklifler
                          </button>
                          <button
                            onClick={() => openPdf(r)}
                            title="PDF Görüntüle"
                            data-testid={`rfq-pdf-${i}`}
                            className="w-8 h-8 flex items-center justify-center hover:bg-slate-100"
                          >
                            <FilePdf size={16} className="text-red-600" />
                          </button>
                          <button
                            onClick={() => nav(`/teklif-talepleri/${r.id}`)}
                            title="Düzenle"
                            data-testid={`rfq-edit-${i}`}
                            className="w-8 h-8 flex items-center justify-center hover:bg-slate-100"
                          >
                            <PencilSimple size={16} className="text-slate-600" />
                          </button>
                          <button
                            onClick={() => del(r)}
                            title="Sil"
                            data-testid={`rfq-del-${i}`}
                            className="w-8 h-8 flex items-center justify-center hover:bg-red-50"
                          >
                            <TrashSimple size={16} className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </Shell>
  );
}
